// SECERA centralized analytics wrapper for GA4 (gtag.js) and TikTok Pixel (ttq)
// Centralizing event tracking ensures consistency and robust execution across the SPA.

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    ttq?: {
      load: (pixelId: string) => void;
      page: () => void;
      track: (event: string, params?: object) => void;
      _t?: any;
    };
  }
}

// Read Measurement IDs from environment variables
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
export const TIKTOK_PIXEL_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID;

const isGAEnabled = () => !!GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX';
const isTikTokEnabled = () => !!TIKTOK_PIXEL_ID && TIKTOK_PIXEL_ID !== 'XXXXXXXXXXXXXXXX';

// Detect local development environment safely (handles localhost, 127.0.0.1, and local network IPs)
const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname === '127.0.0.1' || 
   window.location.hostname.startsWith('192.168.'));

/**
 * Dynamically initializes and injects the Google Analytics and TikTok Pixel scripts if they don't already exist.
 */
export function initAnalytics() {
  if (typeof window === 'undefined') return;

  // 1. Google Analytics Initialisation
  if (isGAEnabled() && !window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function (...args: any[]) {
      window.dataLayer?.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false, // Prevent duplicate automatic pageviews in SPA
      cookie_domain: isLocalhost ? 'none' : 'auto', // Handle cookie domain for localhost vs production
      debug_mode: isLocalhost // Enable DebugView only on local environments to keep production data clean
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  // 2. TikTok Pixel Initialisation
  if (isTikTokEnabled() && !window.ttq) {
    const ttq = window.ttq = window.ttq || {
      load: function (pixelId: string) {
        this.track('PageView');
      },
      page: function () {
        this.track('PageView');
      },
      track: function (event: string, params?: object) {
        (this._t = this._t || []).push([event, params]);
      }
    };
    
    ttq._t = ttq._t || [];

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://analytics.tiktok.com/i18n/pixel/sdk.js?sdkid=' + TIKTOK_PIXEL_ID;
    document.head.appendChild(script);

    // Load custom configuration
    if (typeof ttq.load === 'function') {
      ttq.load(TIKTOK_PIXEL_ID);
    }
  }
}

/**
 * Tracks SPA page views
 * @param path URL path (e.g., '/shop')
 */
export function trackPageView(path: string) {
  if (typeof window === 'undefined') return;

  // GA4 pageview
  if (isGAEnabled() && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: document.title,
      page_location: window.location.href,
      debug_mode: isLocalhost
    });
  }

  // TikTok PageView
  if (isTikTokEnabled() && window.ttq) {
    if (typeof window.ttq.page === 'function') {
      window.ttq.page();
    } else {
      window.ttq.track('PageView');
    }
  }
}

/**
 * Tracks viewing a product detail page
 * @param product The loaded product details
 */
export function trackViewItem(product: any) {
  if (!product) return;
  const price = product.promo_price ?? product.promoPrice ?? product.price ?? 0;

  // GA4 view_item event
  if (isGAEnabled() && window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'IDR',
      value: price,
      debug_mode: isLocalhost,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: price,
          quantity: 1,
          item_category: 'Modest Fashion',
          item_category2: product.material || undefined
        }
      ]
    });
  }

  // TikTok ViewContent event
  if (isTikTokEnabled() && window.ttq) {
    window.ttq.track('ViewContent', {
      contents: [
        {
          id: product.id,
          name: product.name,
          quantity: 1,
          item_type: 'product',
          price: price
        }
      ],
      value: price,
      currency: 'IDR'
    });
  }
}

/**
 * Tracks adding a product variant to the shopping cart
 * @param item The CartItem format
 */
export function trackAddToCart(item: any) {
  if (!item) return;
  const price = item.promoPrice ?? item.price ?? 0;
  const totalValue = price * item.quantity;

  // GA4 add_to_cart event
  if (isGAEnabled() && window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'IDR',
      value: totalValue,
      debug_mode: isLocalhost,
      items: [
        {
          item_id: item.productId,
          item_name: item.productName,
          price: price,
          quantity: item.quantity,
          item_variant: `${item.color || ''} - ${item.option || ''}`.trim(),
          item_category: 'Modest Fashion'
        }
      ]
    });
  }

  // TikTok AddToCart event
  if (isTikTokEnabled() && window.ttq) {
    window.ttq.track('AddToCart', {
      contents: [
        {
          id: item.productId,
          name: item.productName,
          quantity: item.quantity,
          item_type: 'product',
          price: price
        }
      ],
      value: totalValue,
      currency: 'IDR'
    });
  }
}

/**
 * Tracks entering the checkout funnel
 * @param items List of active cart items
 * @param total Total amount to pay
 */
export function trackBeginCheckout(items: any[], total: number) {
  if (!items || items.length === 0) return;

  // GA4 begin_checkout event
  if (isGAEnabled() && window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'IDR',
      value: total,
      debug_mode: isLocalhost,
      items: items.map(item => ({
        item_id: item.productId,
        item_name: item.productName,
        price: item.promoPrice ?? item.price,
        quantity: item.quantity,
        item_variant: `${item.color || ''} - ${item.option || ''}`.trim(),
        item_category: 'Modest Fashion'
      }))
    });
  }

  // TikTok InitiateCheckout event
  if (isTikTokEnabled() && window.ttq) {
    window.ttq.track('InitiateCheckout', {
      contents: items.map(item => ({
        id: item.productId,
        name: item.productName,
        quantity: item.quantity,
        item_type: 'product',
        price: item.promoPrice ?? item.price
      })),
      value: total,
      currency: 'IDR'
    });
  }
}

/**
 * Tracks final order payment success
 * @param order Order detail including purchased items
 */
export function trackPurchase(order: any) {
  if (!order || !order.id) return;

  const trackingKey = `secera_purchase_tracked_${order.id}`;
  if (typeof localStorage !== 'undefined') {
    if (localStorage.getItem(trackingKey) === 'true') {
      console.log(`[Analytics] Purchase for order ${order.id} was already tracked. Skipping.`);
      return;
    }
  }

  const items = order.items || [];
  const coupon = order.voucher_code || undefined;

  // GA4 purchase event
  if (isGAEnabled() && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: String(order.id),
      value: Number(order.total_amount) || 0,
      tax: 0,
      shipping: Number(order.shipping_cost) || 0,
      currency: 'IDR',
      coupon: coupon,
      debug_mode: isLocalhost,
      items: items.map((item: any) => {
        const itemPrice = Number(item.promo_price ?? item.price) || 0;
        return {
          item_id: item.product_id,
          item_name: item.product_name,
          price: itemPrice,
          quantity: Number(item.quantity) || 1,
          item_variant: item.variant_sku || undefined,
          item_category: 'Modest Fashion'
        };
      })
    });
  }

  // TikTok CompletePayment event
  if (isTikTokEnabled() && window.ttq) {
    window.ttq.track('CompletePayment', {
      contents: items.map((item: any) => {
        const itemPrice = Number(item.promo_price ?? item.price) || 0;
        return {
          id: item.product_id,
          name: item.product_name,
          quantity: Number(item.quantity) || 1,
          item_type: 'product',
          price: itemPrice
        };
      }),
      value: Number(order.total_amount) || 0,
      currency: 'IDR'
    });
  }

  // Save to localStorage to prevent duplicate tracking
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(trackingKey, 'true');
    } catch (e) {
      console.warn('[Analytics] Failed to save purchase tracked state to localStorage', e);
    }
  }
}

/**
 * Tracks custom events, especially interactive features like bridesmaid curation or sizing tools.
 */
export function trackCustomEvent(eventName: string, params?: object) {
  if (typeof window === 'undefined') return;

  if (isGAEnabled() && window.gtag) {
    window.gtag('event', eventName, params || {});
  }

  if (isTikTokEnabled() && window.ttq) {
    window.ttq.track(eventName, params || {});
  }
}
